import {
  AppModal,
  Button,
  Form,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { cn } from "@/lib/utils/cn";
import { MatterFileSchema } from "@/lib/data/schemas";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const formSchema = z.object({
  fileName: MatterFileSchema.shape.name,
});

type ConvertFileToPdfFormData = z.infer<typeof formSchema>;

const defaultValues: ConvertFileToPdfFormData = {
  fileName: "",
};

interface ConvertFileToPdfSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sourceFileName: string;
  existingFileNames: string[];
  onConvert: (pdfFileName: string) => Promise<void>;
  title?: string;
  description?: string;
  submitLabel?: string;
  loading?: boolean;
  overlayClassName?: string;
  className?: string;
}

const buildUniquePdfName = (baseName: string, existingFileNames: string[]) => {
  const normalizedExistingNames = new Set(
    existingFileNames.map((name) => name.trim().toLowerCase()),
  );
  const trimmedBaseName = baseName.trim() || "Document";

  const buildFullName = (name: string) => `${name}.pdf`;
  if (
    !normalizedExistingNames.has(buildFullName(trimmedBaseName).toLowerCase())
  ) {
    return trimmedBaseName;
  }

  let index = 1;
  let candidate = `${trimmedBaseName} (${index})`;
  while (normalizedExistingNames.has(buildFullName(candidate).toLowerCase())) {
    index += 1;
    candidate = `${trimmedBaseName} (${index})`;
  }

  return candidate;
};

export const ConvertFileToPdfSlideover = ({
  open,
  setOpen,
  sourceFileName,
  existingFileNames,
  onConvert,
  title = "Convert to PDF",
  description = "Create a PDF copy of this file with a new name.",
  submitLabel = "Create PDF",
  loading = false,
  overlayClassName,
  className,
}: ConvertFileToPdfSlideoverProps) => {
  const formId = "convert-file-to-pdf-form";
  const form = useForm<ConvertFileToPdfFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!open) {
      form.reset(defaultValues);
      return;
    }

    const sourceBaseName = getFileNameParts(sourceFileName).fileName;
    form.reset({
      fileName: buildUniquePdfName(
        sourceBaseName || "Document",
        existingFileNames,
      ),
    });
  }, [existingFileNames, form, open, sourceFileName]);

  const handleSubmit = async (data: ConvertFileToPdfFormData) => {
    const fullFileName = `${data.fileName}.pdf`;
    const hasConflict = existingFileNames.some(
      (name) => name.trim().toLowerCase() === fullFileName.toLowerCase(),
    );

    if (hasConflict) {
      form.setError("fileName", {
        type: "manual",
        message: "A file with this name already exists",
      });
      return;
    }

    try {
      await onConvert(fullFileName);
      setOpen(false);
    } catch (error) {
      console.error("Failed to convert file to PDF", error);
      toast.error("Failed to create PDF copy. Please try again.");
    }
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      isDirty={form.formState.isDirty}
      loading={loading}
      size="lg"
      overlayClassName={overlayClassName}
      className={className}
      footer={
        <div className="flex w-full justify-end gap-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={form.formState.isDirty}
          />
          <Button
            type="submit"
            form={formId}
            disabled={loading || !form.formState.isDirty}
          >
            {loading ? "Saving..." : submitLabel}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id={formId}
          className="relative flex grow flex-col overflow-hidden"
          onSubmit={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await form.handleSubmit(handleSubmit)();
          }}
        >
          {loading && <FormLoadingOverlay />}
          <div className="overflow-y-auto">
            <fieldset
              className={cn(
                slideoverFieldsetClassName,
                "grid-cols-1 md:grid-cols-2",
              )}
            >
              <TextFormField
                register={form.register("fileName")}
                label="File name"
                placeholder="Enter a file name"
              />
              <Typography variant="subHeading" className="mb-3 self-end">
                .pdf
              </Typography>
            </fieldset>
          </div>
        </form>
      </Form>
    </AppModal>
  );
};
